import React, { useState } from 'react';

// Define types for our component
interface XmlToMarkdownProps {}

// XML to Markdown conversion function
const xmlToMarkdown = (xml: string): string => {
  try {
    // Create a DOM parser and parse the XML string
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "application/xml");
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XML Parsing Error: " + parseError[0].textContent);
    }
    
    // Start building Markdown content
    let markdownContent = '# XML Document Structure\n\n';
    
    // Process the root element
    const rootElement = xmlDoc.documentElement;
    markdownContent += processElementToMarkdown(rootElement, 0);
    
    return markdownContent;
  } catch (error) {
    throw error;
  }
};

// Helper function to process an element and generate Markdown
const processElementToMarkdown = (element: Element, depth: number): string => {
  const elementName = element.nodeName;
  let result = '';
  
  // Add element heading based on depth
  const headingLevel = Math.min(depth + 2, 6); // H2 for root, up to H6
  const heading = '#'.repeat(headingLevel);
  result += `${heading} Element: ${elementName}\n\n`;
  
  // Process attributes if any
  const attributes = element.attributes;
  if (attributes && attributes.length > 0) {
    result += "**Attributes:**\n\n";
    result += "| Name | Value |\n|------|-------|\n";
    
    for (let i = 0; i < attributes.length; i++) {
      const attr = attributes[i];
      result += `| ${attr.name} | \`${attr.value}\` |\n`;
    }
    
    result += '\n';
  }
  
  // Process child elements
  const childElements = Array.from(element.children);
  
  if (childElements.length > 0) {
    result += "**Child Elements:**\n\n";
    
    // Track unique child element names and count occurrences
    const childCounts = new Map<string, number>();
    childElements.forEach(child => {
      const childName = child.nodeName;
      childCounts.set(childName, (childCounts.get(childName) || 0) + 1);
    });
    
    // List unique child elements with counts
    result += "| Element | Count |\n|---------|-------|\n";
    childCounts.forEach((count, name) => {
      result += `| ${name} | ${count} |\n`;
    });
    
    result += '\n';
    
    // Process each unique child element (first occurrence only)
    const processedElements = new Set<string>();
    
    for (const child of childElements) {
      const childName = child.nodeName;
      
      // Only process first occurrence of each element type
      if (!processedElements.has(childName)) {
        processedElements.add(childName);
        result += processElementToMarkdown(child, depth + 1);
      }
    }
  } else if (element.textContent && element.textContent.trim() !== "") {
    // Element with just text content
    const text = element.textContent.trim();
    result += "**Content:**\n\n";
    
    // For short content, display as code block
    if (text.length < 100) {
      result += "```\n" + text + "\n```\n\n";
    } else {
      // For longer content, use a truncated version
      result += "```\n" + text.substring(0, 97) + "...\n```\n\n";
      result += `*Content truncated (${text.length} characters total)*\n\n`;
    }
  } else {
    // Empty element
    result += "*This element is empty*\n\n";
  }
  
  // Add separator between elements
  result += "---\n\n";
  
  return result;
};

// Alternative converter that creates a more data-focused Markdown representation
const xmlToDataMarkdown = (xml: string): string => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "application/xml");
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XML Parsing Error: " + parseError[0].textContent);
    }
    
    // Start with a main title
    let markdown = `# ${xmlDoc.documentElement.nodeName}\n\n`;
    
    // Process the document
    markdown += processElementAsData(xmlDoc.documentElement, 1);
    
    return markdown;
  } catch (error) {
    throw error;
  }
};

// Process elements with emphasis on the data structure
const processElementAsData = (element: Element, level: number): string => {
  let result = '';
  const indentSpace = '  '.repeat(level - 1);
  
  // Add attributes as key-value pairs if they exist
  if (element.attributes.length > 0) {
    result += `${indentSpace}**Attributes:**\n\n`;
    
    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      result += `${indentSpace}- \`${attr.name}\`: "${attr.value}"\n`;
    }
    
    result += '\n';
  }
  
  // Get all child elements
  const childElements = Array.from(element.children);
  
  // Group children by name to handle arrays
  const childrenByName = new Map<string, Element[]>();
  
  childElements.forEach(child => {
    const name = child.nodeName;
    if (!childrenByName.has(name)) {
      childrenByName.set(name, []);
    }
    childrenByName.get(name)!.push(child);
  });
  
  // Process each group of children
  childrenByName.forEach((elements, name) => {
    // Check if this is an array of elements (multiple with same name)
    const isArray = elements.length > 1;
    
    if (isArray) {
      result += `${indentSpace}## ${name} (${elements.length} items)\n\n`;
      
      // Check if these are simple elements
      const isSimple = elements.every(el => 
        el.children.length === 0 && el.textContent && el.textContent.trim() !== ""
      );
      
      if (isSimple) {
        // If simple elements, display as a list
        result += `${indentSpace}Values:\n\n`;
        elements.forEach((el, index) => {
          result += `${indentSpace}- ${el.textContent!.trim()}\n`;
        });
        result += '\n';
      } else {
        // Display as separate structured items
        elements.forEach((el, index) => {
          result += `${indentSpace}### Item ${index + 1}\n\n`;
          result += processElementAsData(el, level + 1);
        });
      }
    } else {
      // Single element
      const element = elements[0];
      
      // Check if it's a simple text element
      if (element.children.length === 0 && element.textContent && element.textContent.trim() !== "") {
        result += `${indentSpace}## ${name}: ${element.textContent.trim()}\n\n`;
      } else {
        result += `${indentSpace}## ${name}\n\n`;
        result += processElementAsData(element, level + 1);
      }
    }
  });
  
  // If this element has direct text content but no children, display it
  if (childElements.length === 0 && element.textContent && element.textContent.trim() !== "") {
    result += `${indentSpace}Value: ${element.textContent.trim()}\n\n`;
  }
  
  return result;
};

// Helper function that creates a table of contents from XML
const generateTOC = (xml: string): string => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "application/xml");
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XML Parsing Error: " + parseError[0].textContent);
    }
    
    let toc = "# Table of Contents\n\n";
    toc += buildTOCForElement(xmlDoc.documentElement, 0);
    
    return toc;
  } catch (error) {
    throw error;
  }
};

// Build table of contents recursively
const buildTOCForElement = (element: Element, depth: number): string => {
  const indent = '  '.repeat(depth);
  let result = `${indent}- [${element.nodeName}](#element-${element.nodeName.toLowerCase()})\n`;
  
  // Add all immediate children to TOC
  const childElements = Array.from(element.children);
  const processedElements = new Set<string>();
  
  for (const child of childElements) {
    const childName = child.nodeName;
    
    // Only process first occurrence of each element type in TOC
    if (!processedElements.has(childName)) {
      processedElements.add(childName);
      result += buildTOCForElement(child, depth + 1);
    }
  }
  
  return result;
};

// Main component to convert XML to Markdown
const XmlToMarkdown: React.FC<XmlToMarkdownProps> = () => {
  // State for the XML input
  const [xmlInput, setXmlInput] = useState<string>('');
  // State for the Markdown output
  const [markdownOutput, setMarkdownOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('document');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isMarkdownGenerated, setIsMarkdownGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for conversion mode
  const [conversionMode, setConversionMode] = useState<string>('structure');
  // State for including TOC option
  const [includeTOC, setIncludeTOC] = useState<boolean>(true);
  // State for max content length
  const [maxContentLength, setMaxContentLength] = useState<number>(100);

  // Handle XML input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setXmlInput(e.target.value);
    setError('');
    setIsMarkdownGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    const exampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<library>
  <book id="bk101" category="fiction">
    <title>The Great Gatsby</title>
    <author>F. Scott Fitzgerald</author>
    <year>1925</year>
    <price>9.99</price>
    <tags>
      <tag>classic</tag>
      <tag>american literature</tag>
      <tag>jazz age</tag>
    </tags>
    <available>true</available>
  </book>
  <book id="bk102" category="non-fiction">
    <title>A Brief History of Time</title>
    <author>Stephen Hawking</author>
    <year>1988</year>
    <price>12.50</price>
    <tags>
      <tag>science</tag>
      <tag>physics</tag>
      <tag>cosmology</tag>
    </tags>
    <available>false</available>
  </book>
</library>`;

    setXmlInput(exampleXml);
    setError('');
    setIsMarkdownGenerated(false);
    setMarkdownOutput('');
  };

  // Format/Beautify XML
  const formatXml = () => {
    try {
      if (!xmlInput.trim()) {
        setError('Please enter XML data to format');
        return;
      }
      
      // Parse the XML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlInput, "application/xml");
      
      // Check for parsing errors
      const parseError = xmlDoc.getElementsByTagName("parsererror");
      if (parseError.length > 0) {
        throw new Error("XML Parsing Error: " + parseError[0].textContent);
      }
      
      // Serialize back to string with pretty formatting
      const serializer = new XMLSerializer();
      let formattedXml = '';
      
      // Function to pretty print XML with indentation
      const prettyPrintXml = (node: Node, indent: string = ''): string => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.nodeValue?.trim();
          return text ? indent + text + '\n' : '';
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          let result = indent + '<' + element.nodeName;
          
          // Add attributes
          for (let i = 0; i < element.attributes.length; i++) {
            const attr = element.attributes[i];
            result += ` ${attr.nodeName}="${attr.nodeValue}"`;
          }
          
          // Check if there are any child nodes
          if (element.childNodes.length === 0) {
            result += '/>\n';
            return result;
          }
          
          result += '>\n';
          
          // Process child nodes
          for (let i = 0; i < element.childNodes.length; i++) {
            result += prettyPrintXml(element.childNodes[i], indent + '  ');
          }
          
          result += indent + '</' + element.nodeName + '>\n';
          return result;
        }
        
        return '';
      };
      
      // Add XML declaration
      formattedXml += '<?xml version="1.0" encoding="UTF-8"?>\n';
      
      // Format the document element
      formattedXml += prettyPrintXml(xmlDoc.documentElement);
      
      setXmlInput(formattedXml);
      setError('');
    } catch (e) {
      setError(`Error formatting XML: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Convert XML to Markdown
  const convertXmlToMarkdown = () => {
    setError('');
    setIsLoading(true);
    setIsMarkdownGenerated(false);
    setMarkdownOutput('');
    
    if (!xmlInput.trim()) {
      setError('Please enter XML data');
      setIsLoading(false);
      return;
    }

    try {
      let markdown = '';
      
      // Generate table of contents if enabled
      if (includeTOC) {
        markdown += generateTOC(xmlInput) + "\n\n";
      }
      
      // Generate markdown based on selected mode
      if (conversionMode === 'structure') {
        markdown += xmlToMarkdown(xmlInput);
      } else {
        markdown += xmlToDataMarkdown(xmlInput);
      }
      
      setMarkdownOutput(markdown);
      setIsMarkdownGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting XML to Markdown: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setXmlInput('');
    setMarkdownOutput('');
    setError('');
    setFileName('document');
    setIncludeTOC(true);
    setConversionMode('structure');
    setMaxContentLength(100);
    setIsMarkdownGenerated(false);
  };

  // Copy Markdown to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(markdownOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download Markdown file
  const downloadMarkdown = () => {
    const blob = new Blob([markdownOutput], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'document'}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">XML to Markdown Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Generate structured Markdown documentation from XML data. Paste your XML below to create a Markdown document.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="xmlInput" className="font-medium text-gray-700">
                XML Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={formatXml}
                >
                  Format XML
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setXmlInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="xmlInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={xmlInput}
              onChange={handleInputChange}
              placeholder="Paste your XML data here, e.g.,
<?xml version='1.0' encoding='UTF-8'?>
<library>
  <book>
    <title>Sample Book</title>
    <author>John Doe</author>
  </book>
</library>"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                Markdown File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="conversionMode" className="block text-sm font-medium text-gray-700 mb-1">
                Conversion Mode
              </label>
              <select
                id="conversionMode"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={conversionMode}
                onChange={(e) => setConversionMode(e.target.value)}
              >
                <option value="structure">Structure-focused (element hierarchy)</option>
                <option value="data">Data-focused (content-oriented)</option>
              </select>
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeTOC"
                    checked={includeTOC}
                    onChange={(e) => setIncludeTOC(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeTOC" className="ml-2 block text-sm text-gray-700">
                    Include Table of Contents
                  </label>
                </div>
                
                <div>
                  <label htmlFor="maxContentLength" className="block text-sm font-medium text-gray-700 mb-1">
                    Max Content Display Length (characters)
                  </label>
                  <input
                    type="number"
                    id="maxContentLength"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={maxContentLength}
                    onChange={(e) => setMaxContentLength(parseInt(e.target.value))}
                    min="10"
                    max="1000"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Long text content will be truncated to this length in the Markdown output
                  </p>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertXmlToMarkdown}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Markdown'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Markdown Output
            </label>
            
            {isMarkdownGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Markdown'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadMarkdown}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isMarkdownGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    Markdown output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isMarkdownGenerated && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="markdownOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={markdownOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isMarkdownGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">XML Successfully Converted to Markdown!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated Markdown documentation.
              </p>
            </div>
          )}
        </div>
      </div>
      
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About XML to Markdown Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your XML data into a formatted Markdown document. XML's hierarchical structure is preserved in the Markdown output,
              with elements transformed into headings, lists, and tables as appropriate. XML attributes are preserved in a readable format, and text content is handled intelligently.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Smart Conversion</strong> - Handles complex XML structures with intelligent formatting</li>
              <li><strong>Attribute Preservation</strong> - XML attributes are clearly displayed in the Markdown output</li>
              <li><strong>Structured Headings</strong> - Automatic organization with appropriate heading levels</li>
              <li><strong>Lists and Tables</strong> - Automatic conversion of repeated elements to lists or tables</li>
              <li><strong>Error Handling</strong> - Clear error messages for invalid XML input</li>
              <li><strong>Easy Download</strong> - Save the converted Markdown as a file with one click</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">XML to Markdown Conversion Rules</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>XML element names become Markdown headings</li>
              <li>XML attributes are displayed as lists or tables depending on complexity</li>
              <li>Element text content is preserved in the appropriate context</li>
              <li>Multiple XML elements with the same name are formatted as lists</li>
              <li>Complex nested elements maintain their hierarchy with appropriate heading levels</li>
              <li>Empty XML elements are marked as such</li>
              <li>XML comments are ignored in the conversion process</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format XML" button to check if your XML is valid before conversion</li>
              <li>XML is case-sensitive, so elements with different cases are treated as different elements</li>
              <li>Choose "Structured" conversion style for more organized documentation</li>
              <li>Choose "Simple" conversion style for a more linear representation</li>
              <li>Enable "Use Tables for Complex Data" for better visualization of complex elements</li>
              <li>The structure of the Markdown output closely follows the structure of the XML input</li>
              <li>Use the example button to see a properly formatted XML example</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Markdown Preview Section (when available) */}
      {isMarkdownGenerated && (
        <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Markdown Preview</h2>
          <div 
            className="prose max-w-none p-4 border border-gray-200 rounded-lg bg-gray-50"
            dangerouslySetInnerHTML={{ 
              __html: markdownOutput
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;')
                .replace(/\n/g, '<br>')
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                .replace(/#{6}\s(.*?)(?:\n|$)/g, '<h6>$1</h6>')
                .replace(/#{5}\s(.*?)(?:\n|$)/g, '<h5>$1</h5>')
                .replace(/#{4}\s(.*?)(?:\n|$)/g, '<h4>$1</h4>')
                .replace(/#{3}\s(.*?)(?:\n|$)/g, '<h3>$1</h3>')
                .replace(/#{2}\s(.*?)(?:\n|$)/g, '<h2>$1</h2>')
                .replace(/#{1}\s(.*?)(?:\n|$)/g, '<h1>$1</h1>')
                .replace(/\|\s(.*?)\s\|/g, '<table><tr><td>$1</td></tr></table>')
                .replace(/(-|\*)\s(.*?)(?:\n|$)/g, '<ul><li>$2</li></ul>')
                .replace(/`(.*?)`/g, '<code>$1</code>')
            }}
          />
        </div>
      )}
    </div>
  );
};

export default XmlToMarkdown;